/**
 * Runners Service
 * 
 * Service for making API calls related to runners.
 * Replaces Apollo GraphQL queries with REST API calls.
 * Handles the existing API response format and transforms it to match GraphQL structure.
 */

import service from './index'

const resource = service.resource('runners')

export default {
  /**
   * Get all runners with pagination and search
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 50)
   * @param {string} params.search - Search term (default: '')
   * @returns {Promise} API response transformed to GraphQL format
   */
  async getAllRunners(params = {}) {
    const { page = 1, limit = 50, search = '' } = params
    
    try {
      // Call the existing API with correct parameters
      // The existing API uses 0-based page numbering and different parameter names
      const queryParams = {
        page: page - 1, // Convert to 0-based page numbering
        size: limit,
        sort: 'id,desc'
      }
      
      // Add search parameters if provided
      if (search && search.trim()) {
        // Try common search parameter names that might work with the existing API
        queryParams.search = search.trim()
        // Also try other possible parameter names
        queryParams.q = search.trim()
        queryParams.query = search.trim()
      }
      
      console.log('Calling API with params:', queryParams)
      const response = await resource.query(queryParams)
      console.log('Raw API response:', response)
      
      // Transform the response to match GraphQL structure
      const transformedResponse = this.transformApiResponse(response.body, page)
      console.log('Transformed response:', transformedResponse)
      
      return {
        body: {
          success: true,
          data: {
            AllRunners: transformedResponse
          }
        }
      }
    } catch (error) {
      console.error('Error fetching runners:', error)
      throw error
    }
  },

  /**
   * Transform API response to match GraphQL structure
   * @param {Object} apiResponse - Original API response
   * @param {number} currentPage - Current page number (1-based)
   * @returns {Object} Transformed response matching GraphQL format
   */
  transformApiResponse(apiResponse, currentPage) {
    if (!apiResponse || !apiResponse.content) {
      return {
        count: 0,
        pagination: { totalPages: 0 },
        runners: []
      }
    }

    const runners = apiResponse.content.map(runner => this.transformRunnerData(runner))

    return {
      count: apiResponse.totalElements || 0,
      pagination: {
        totalPages: apiResponse.totalPages || 0,
        currentPage: currentPage,
        hasNext: !apiResponse.last,
        hasPrev: !apiResponse.first
      },
      runners: runners
    }
  },

  /**
   * Transform individual runner data to match GraphQL structure
   * @param {Object} runner - Original runner data from API
   * @returns {Object} Transformed runner data
   */
  transformRunnerData(runner) {
    return {
      id: runner.id,
      name: runner.firstName || '',
      location: runner.lastName || '',
      gender: runner.gender || '',
      facebook: !!runner.socialLogin,
      height: runner.height || null,
      weight: runner.weight || null,
      profileText: runner.profileText || '',
      website: runner.website || '',
      
      user: {
        email: (runner.user && runner.user.email) || '',
        accountCreate: (runner.user && runner.user.resetDate) ? 
          new Date(runner.user.resetDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
          }).replace(/\//g, '/') : ''
      },
      
      membership: runner.memberShip ? {
        startDate: runner.memberShip.trialStartDate ? 
          new Date(runner.memberShip.trialStartDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        endDate: runner.memberShip.trialEndDate ? 
          new Date(runner.memberShip.trialEndDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        promoStartDate: (runner.memberShip.promoCode && runner.memberShip.promoCode.expirationDate) ? 
          new Date(runner.memberShip.trialStartDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        promoEndDate: (runner.memberShip.promoCode && runner.memberShip.promoCode.expirationDate) ? 
          new Date(runner.memberShip.promoCode.expirationDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null,
        promoCode: (runner.memberShip.promoCode && runner.memberShip.promoCode.code) || null,
        expirationDate: (runner.memberShip.promoCode && runner.memberShip.promoCode.expirationDate) ? 
          new Date(runner.memberShip.promoCode.expirationDate).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).replace(/\//g, '/') : null
      } : null,
      
      runner_report: {
        totalCompletedRuns: 0, // This data is not available in the current API
        lastLiveRun: null,     // This data is not available in the current API
        daysSinceLastLiveRun: 0 // This data is not available in the current API
      },
      
      promo_report: null // This data is not available in the current API
    }
  },

  /**
   * Get runner by ID
   * @param {number} id - Runner ID
   * @returns {Promise} API response
   */
  getRunnerById(id) {
    return resource.get({ id })
  },

  /**
   * Refresh/refetch runners data
   * Same as getAllRunners but with explicit naming for compatibility
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  refetch(params = {}) {
    return this.getAllRunners(params)
  }
}



// WEBPACK FOOTER //
// ./src/services/runnersService.js